import { Link } from 'react-router-dom';
import { Heart, FileText } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEOHead from '../components/SEOHead';

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SEOHead title="Terms of Use" description="LusoLove terms of use. Read our terms and conditions for using the Portuguese dating platform." path="/terms" />
      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#FAF9F6]/90 border-b border-[#E5E3DB]/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo">
            <Heart className="w-7 h-7 text-[#6E1A24] group-hover:scale-110 transition-transform" strokeWidth={1.5} fill="currentColor" fillOpacity={0.1} />
            <span className="text-xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <div className="pt-28 pb-20 px-6 sm:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[#6E1A24]/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#6E1A24]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-serif font-light text-[#2D2D2A]" data-testid="terms-title">
                Terms of Use
              </h1>
              <p className="text-sm text-[#6C6C67] mt-1">Last updated: 09.04.2026</p>
            </div>
          </div>

          <div className="space-y-10">
            <Section number="1" title="Eligibility">
              <p className="text-[#6C6C67] leading-relaxed">Users must be 18+.</p>
            </Section>

            <Section number="2" title="Account">
              <p className="text-[#6C6C67] leading-relaxed">Users are responsible for their account.</p>
            </Section>

            <Section number="3" title="User Conduct">
              <p className="text-[#6C6C67] leading-relaxed mb-3">Users must not:</p>
              <ul className="list-disc list-inside space-y-2 text-[#6C6C67] leading-relaxed">
                <li>Harass others</li>
                <li>Share illegal content</li>
                <li>Impersonate others</li>
                <li>Scam or abuse the platform</li>
              </ul>
            </Section>

            <Section number="4" title="Content">
              <p className="text-[#6C6C67] leading-relaxed">
                Users are responsible for their content. We can remove content violating rules.
              </p>
            </Section>

            <Section number="5" title="Matching & Communication">
              <p className="text-[#6C6C67] leading-relaxed">No guarantee of matches or outcomes.</p>
            </Section>

            <Section number="6" title="Subscriptions">
              <p className="text-[#6C6C67] leading-relaxed">
                Premium and Gold subscriptions available. Payments handled via Braintree (PayPal). Subscriptions may auto-renew unless canceled.
              </p>
            </Section>

            <Section number="7" title="Termination">
              <p className="text-[#6C6C67] leading-relaxed">Accounts may be suspended if rules are violated.</p>
            </Section>

            <Section number="8" title="Liability">
              <p className="text-[#6C6C67] leading-relaxed">
                Service provided "as is". We are not responsible for user interactions.
              </p>
            </Section>

            <Section number="9" title="Contact">
              <p className="text-[#6C6C67] leading-relaxed">
                <a href="mailto:contact@lusolove.com" className="text-[#6E1A24] hover:underline">
                  contact@lusolove.com
                </a>
              </p>
            </Section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 bg-[#2D2D2A] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6 text-[#C97A63]" strokeWidth={1.5} fill="currentColor" fillOpacity={0.3} />
            <span className="text-xl font-serif">LusoLove</span>
          </div>
          <div className="flex gap-8 text-sm text-white/60">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
            <Link to="/refund" className="hover:text-white transition-colors">Refund Policy</Link>
            <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
          <p className="text-sm text-white/40">Made in Portugal, with love.</p>
        </div>
      </footer>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <div data-testid={`terms-section-${number}`}>
      <h2 className="text-lg font-serif font-medium text-[#2D2D2A] mb-3">
        <span className="text-[#6E1A24] mr-2">{number}.</span>
        {title}
      </h2>
      {children}
    </div>
  );
}
